import { ref, unref, type Ref, type ComputedRef } from 'vue'
import { toast } from 'vue-sonner'

export function useMarkdownPdf(
  markdownContent: Ref<string> | ComputedRef<string>, // Mantenido por retrocompatibilidad
  filename: string | Ref<string>,
  documentId?: string | Ref<string> | ComputedRef<string>,
  targetSelector = '.md-editor-preview'
) {
  const isGenerating = ref(false)

  async function downloadPdf(event?: Event) {
    isGenerating.value = true

    try {
      // Intentar encontrar el elemento a imprimir basándose en el evento (soporta múltiples editores en pantalla)
      let targetElement: HTMLElement | null = null
      
      if (event && event.target) {
        const container = (event.target as HTMLElement).closest('.fixed, .dialog, main, #app')
        if (container) {
          targetElement = container.querySelector(targetSelector)
        }
      }
      
      if (!targetElement) {
        targetElement = document.querySelector(targetSelector)
      }

      if (!targetElement) {
        toast.error('No se encontró el visualizador de documentos para imprimir.')
        isGenerating.value = false
        return
      }

      const style = document.createElement('style')
      style.innerHTML = `
        @media print {
          @page {
            size: A4;
            margin: 15mm 15mm 20mm 15mm;
          }
          
          body > * { display: none !important; }
          body > .print-path, body > .print-target { display: block !important; }
          
          .print-path > * { display: none !important; }
          .print-path > .print-path, .print-path > .print-target { display: block !important; }
          
          /* Evitar que las tablas y bloques de código se corten a la derecha */
          .print-target * {
            overflow: visible !important;
          }
          .print-target table {
            width: calc(100% - 2px) !important;
            margin: 0 1px !important;
          }
          .print-target pre, .print-target code {
            white-space: pre-wrap !important;
            word-wrap: break-word !important;
          }
          
          .print-path {
            position: static !important;
            overflow: visible !important;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            transform: none !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          
          .print-target {
            position: static !important;
            overflow: visible !important;
            height: auto !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            zoom: 0.80; /* Reduce proportionally the size of all text and elements by 20% */
          }
        }
      `
      document.head.appendChild(style)
      
      targetElement.classList.add('print-target')
      
      let current = targetElement.parentElement
      const pathElements: HTMLElement[] = []
      while (current && current !== document.body && current !== document.documentElement) {
        current.classList.add('print-path')
        pathElements.push(current)
        current = current.parentElement
      }
      
      const resolvedFilename = unref(filename) || 'documento'
      
      const originalTitle = document.title
      // Usamos un título limpio para que el encabezado del PDF se vea bien
      document.title = resolvedFilename

      // Guardamos la URL original para restaurarla
      const originalUrl = window.location.href
      // Disfrazamos la URL temporalmente para que en el pie de página salga algo limpio
      history.replaceState({}, '', '/documento')

      // Forzar modo claro temporalmente para impresión si está en oscuro
      const htmlEl = document.documentElement
      const wasDark = htmlEl.classList.contains('dark')
      if (wasDark) htmlEl.classList.remove('dark')

      // Pequeño delay para asegurar que el DOM actualizó
      await new Promise(resolve => setTimeout(resolve, 150))

      window.print()

      // Restaurar todo a la normalidad
      if (wasDark) htmlEl.classList.add('dark')
      history.replaceState({}, '', originalUrl)
      document.title = originalTitle
      document.head.removeChild(style)
      targetElement.classList.remove('print-target')
      pathElements.forEach(el => el.classList.remove('print-path'))

      toast.success('Usa "Guardar como PDF" en el diálogo de impresión')
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Error al preparar la impresión')
    } finally {
      isGenerating.value = false
    }
  }

  return {
    isGenerating,
    downloadPdf,
  }
}
